import { getMetaMatchKeys } from "@/lib/utils/acquisitionContext";

// Extend Navigator interface for deviceMemory
declare global {
  interface Navigator {
    deviceMemory?: number;
  }
}

const PUBLIC_IS_PROD = import.meta.env.VITE_PUBLIC_IS_PROD;
const PUBLIC_IS_LOCAL = import.meta.env.VITE_PUBLIC_IS_LOCAL;
const PUBLIC_API_URL = import.meta.env.VITE_PUBLIC_API_URL;

const API_URL = PUBLIC_API_URL
  ? `${PUBLIC_API_URL}/api/v1/auth`
  : PUBLIC_IS_PROD === "TRUE"
  ? `https://app.humblytics.com/api/v1/auth`
  : PUBLIC_IS_LOCAL === "TRUE"
  ? `http://localhost:3000/api/v1/auth`
  : `https://humblytics.onrender.com/api/v1/auth`;

interface AuthResponse {
  authToken: string;
  isNew: boolean;
  noSites: boolean;
  hasSubscription: boolean;
}

interface GoogleResponse {
  credential: string;
  /** GSI selection surface — e.g. `btn`, `user_1tap`, `auto` (One Tap attribution). */
  select_by?: string;
}

const postRequest = async (endpoint: string, data: any) => {
  const payload = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  };

  const response = await fetch(API_URL + endpoint, payload);
  return response;
};

const getFingerprint = () => {
  return JSON.stringify([
    navigator.hardwareConcurrency,
    navigator.deviceMemory,
    navigator.maxTouchPoints,
  ]);
};

const hasDebugCookie = () => {
  return document.cookie
    .split(";")
    .some((c) => c.trim().startsWith("humblytics_debug="));
};

const verifyMagicLink = async (
  token: string,
  password: string,
  newUser: boolean
) => {
  const fingerprint = getFingerprint();
  const data = {
    t: token,
    p: password,
    fingerprint,
  };
  const resp = await postRequest(
    newUser ? "magic-register-link" : "/magic-sign-in-link",
    data
  );
  if (resp.ok) {
    const { authToken } = await resp.json();
    return authToken;
  }
  return false;
};

/**
 * Sign in to a Humblytics account
 * @param email - User's email address
 * @param recaptchaToken - Google reCAPTCHA v3 token
 * @returns Promise<boolean>
 */
export const signIn = async (email: string, recaptchaToken: string) => {
  const fingerprint = getFingerprint();
  const data = {
    email,
    fingerprint,
    turnstileToken: recaptchaToken,
  };

  const endpoint = !hasDebugCookie() ? "/sign-in" : "/login";
  const resp = await postRequest(endpoint, data);
  if (resp.ok) {
    return true;
  }
  return false;
};

/**
 * Register for a Humblytics account
 * @param email - User's email address
 * @param name - User's full name
 * @param recaptchaToken - Google reCAPTCHA v3 token
 * @returns Promise<AuthResponse | boolean> - Returns AuthResponse if immediate auth (passwordless), true if magic link sent, false if error
 */
export const register = async (
  email: string,
  name: string,
  recaptchaToken: string
): Promise<AuthResponse | { codeSent: true; isNew: boolean } | false> => {
  const fingerprint = getFingerprint();
  const data = {
    email,
    name,
    fingerprint,
    turnstileToken: recaptchaToken,
  };

  const endpoint = !hasDebugCookie() ? "/register" : "/signup";
  const resp = await postRequest(endpoint, data);
  if (resp.ok) {
    const contentType = resp.headers.get("content-type");
    if (contentType && contentType.includes("application/json")) {
      const result = await resp.json();
      // Immediate auth token (passwordless / debug flow)
      if (result.authToken) {
        return {
          authToken: result.authToken,
          isNew: result.isNew !== undefined ? result.isNew : true,
          noSites: result.noSites !== undefined ? result.noSites : true,
          hasSubscription:
            result.hasSubscription !== undefined
              ? result.hasSubscription
              : false,
        };
      }
      // OTP code sent — backend now returns { isNew }
      return { codeSent: true, isNew: !!result.isNew };
    }
    // Fallback for plain-text 200 (shouldn't happen post-backend change)
    return { codeSent: true, isNew: false };
  }
  return false;
};

/**
 * Verify a registered account
 * @param email - User's email address
 * @param code - Verification string sent to email
 * @returns Promise<string | false> - authToken if successful, false if error
 */
export const verify = async (email: string, code: string): Promise<{ authToken: string; isNew: boolean } | false> => {
  const fingerprint = getFingerprint();
  const data = {
    email,
    code,
    fingerprint,
    ...getMetaMatchKeys(),
  };

  const resp = await postRequest("/verify", data);
  if (resp.ok) {
    const { authToken, isNew } = await resp.json();
    if (authToken) {
      return { authToken, isNew: !!isNew };
    } else {
      return false;
    }
  }
  return false;
};

/**
 * Register without email verification (creates account immediately, emailVerified=false).
 * Sends "account already exists" email to existing users.
 * Returns AuthResponse on success, 'user_exists' if email already taken, false on error.
 */
const registerV2 = async (
  email: string,
  name: string,
  recaptchaToken: string
): Promise<AuthResponse | 'user_exists' | false> => {
  const fingerprint = getFingerprint();
  const resp = await postRequest('/register-v2', {
    email,
    name,
    fingerprint,
    turnstileToken: recaptchaToken,
    ...getMetaMatchKeys(),
  });
  if (resp.status === 409) return 'user_exists';
  if (resp.ok) {
    const result = await resp.json();
    if (result.authToken) {
      return {
        authToken: result.authToken,
        isNew: result.isNew !== undefined ? result.isNew : true,
        noSites: result.noSites !== undefined ? result.noSites : true,
        hasSubscription: result.hasSubscription !== undefined ? result.hasSubscription : false,
      };
    }
  }
  return false;
};

/**
 * Silent account creation — no emails sent to anyone.
 * Used exclusively by the "skip email verification" escape hatch on /verify (signup flow only).
 * Returns AuthResponse on success, 'user_exists' if email already taken, false on error.
 */
export const registerV3 = async (
  email: string,
  name: string,
  recaptchaToken: string
): Promise<AuthResponse | 'user_exists' | false> => {
  const fingerprint = getFingerprint();
  const resp = await postRequest('/register-v3', {
    email,
    name,
    fingerprint,
    turnstileToken: recaptchaToken,
    ...getMetaMatchKeys(),
  });
  if (resp.status === 409) return 'user_exists';
  if (resp.ok) {
    const result = await resp.json();
    if (result.authToken) {
      return {
        authToken: result.authToken,
        isNew: result.isNew !== undefined ? result.isNew : true,
        noSites: result.noSites !== undefined ? result.noSites : true,
        hasSubscription: result.hasSubscription !== undefined ? result.hasSubscription : false,
      };
    }
  }
  return false;
};

/**
 * Google authentication
 * @param token - Google credential token
 * @returns Promise<AuthResponse | false>
 */
export const googleAuth = async (
  token: string
): Promise<AuthResponse | false> => {
  const fingerprint = getFingerprint();
  const data = {
    token,
    fingerprint,
    ...getMetaMatchKeys(),
  };

  const resp = await postRequest("/google", data);
  if (resp.ok) {
    const { authToken, isNew, hasSubscription, noSites } = await resp.json();
    if (authToken) {
      return { authToken, isNew, hasSubscription, noSites };
    } else {
      return false;
    }
  }
  return false;
};

export type { AuthResponse, GoogleResponse };
